// src/hooks/usePlatformRole.ts
// Returns the current user's platform role. Fetches membership once and caches.

import { useState, useEffect } from 'react';
import { authStore } from '@core/stores';
import { ListPlatformMembers } from '@core/api/endpoints/platform/list_platform_members';
import type { PlatformRole } from '@core/types/platform';
import { useScope } from './useScope';

let cachedRole: PlatformRole | null = null;
let cacheProfileId: string | null = null;

/**
 * Returns the current user's platform role.
 * Admins: PLATFORM_ROLE_OWNER, PLATFORM_ROLE_ADMIN, PLATFORM_ROLE_MANAGER
 * Regular: PLATFORM_ROLE_MEMBER, PLATFORM_ROLE_VIEWER
 */
export function usePlatformRole(): { role: PlatformRole | null; isAdmin: boolean; isLoading: boolean } {
  const { platformId } = useScope();
  const profileId = authStore.activeProfileId;
  const [role, setRole] = useState<PlatformRole | null>(
    profileId && profileId === cacheProfileId ? cachedRole : null,
  );
  const [isLoading, setIsLoading] = useState(role === null);

  useEffect(() => {
    if (!platformId || !profileId) return;

    // Use cache if available for this profile
    if (cachedRole && cacheProfileId === profileId) {
      setRole(cachedRole);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const resp = await ListPlatformMembers(platformId);
        const members = resp.items?.items ?? [];
        const me = members.find((m) => m.profile?.id === profileId);
        const myRole = me?.role ?? 'PLATFORM_ROLE_MEMBER';
        if (!cancelled) {
          cachedRole = myRole;
          cacheProfileId = profileId;
          setRole(myRole);
        }
      } catch {
        // Default to member on error — safe fallback
        if (!cancelled) {
          setRole('PLATFORM_ROLE_MEMBER');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [platformId, profileId]);

  const isAdmin = role === 'PLATFORM_ROLE_OWNER'
    || role === 'PLATFORM_ROLE_ADMIN'
    || role === 'PLATFORM_ROLE_MANAGER';

  return { role, isAdmin, isLoading };
}
